import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as fabric from 'fabric';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { ChevronLeft, ChevronRight, Maximize2, X, Play, Pause } from 'lucide-react';
import { TRANSITION_CSS, runObjectAnimations, getTransitionAnimation } from './slideAnimations';

const CW = 960;
const CH = 540;

/* ── URL Detection Helpers ── */
const isYouTubeUrl = (url) => {
  if (!url) return false;
  return /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)/.test(url);
};

const isVimeoUrl = (url) => {
  if (!url) return false;
  return /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)/.test(url);
};

const getYouTubeEmbedUrl = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (!match) return null;
  
  // Use youtube-nocookie.com for better privacy and iOS compatibility
  // Add playsinline=1 for iOS WebView, origin for Capacitor apps
  const params = new URLSearchParams({
    autoplay: '1',
    rel: '0',
    modestbranding: '1',
    playsinline: '1', // Required for iOS
    enablejsapi: '1',
    // origin helps with Capacitor - use * to allow any origin
  });
  
  return `https://www.youtube-nocookie.com/embed/${match[1]}?${params.toString()}`;
};

const getVimeoEmbedUrl = (url) => {
  const match = url.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);
  return match ? `https://player.vimeo.com/video/${match[1]}?autoplay=1` : null;
};

const isEmbedUrl = (url) => isYouTubeUrl(url) || isVimeoUrl(url);

const getEmbedUrl = (url) => {
  if (isYouTubeUrl(url)) return getYouTubeEmbedUrl(url);
  if (isVimeoUrl(url)) return getVimeoEmbedUrl(url);
  return null;
};

/* ── Custom Video Player (Play/Pause only, no download) ── */
const MinimalVideoPlayer = ({ src, autoPlay, className, testId, onRef }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(autoPlay);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    if (onRef && videoRef.current) onRef(videoRef.current);
  }, [onRef]);

  useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play().catch(() => setPlaying(false));
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing]);

  const togglePlay = () => setPlaying((p) => !p);

  return (
    <div 
      className={`relative ${className}`} 
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      onClick={togglePlay}
      style={{ cursor: 'pointer' }}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay={autoPlay}
        playsInline
        muted={autoPlay} /* Muted required for autoplay in most browsers */
        className="w-full h-full object-contain bg-black rounded"
        data-testid={testId}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {/* Minimal play/pause overlay */}
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-opacity ${showControls || !playing ? 'opacity-100' : 'opacity-0'}`}
        style={{ pointerEvents: 'none' }}
      >
        <div className="bg-black/50 rounded-full p-3">
          {playing ? (
            <Pause className="w-8 h-8 text-white" />
          ) : (
            <Play className="w-8 h-8 text-white" />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Embed Player (YouTube/Vimeo) ── */
const EmbedPlayer = ({ url, className, testId }) => {
  const embedUrl = getEmbedUrl(url);
  if (!embedUrl) return null;
  
  return (
    <iframe
      src={embedUrl}
      className={`w-full h-full rounded ${className}`}
      data-testid={testId}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
      title="Embedded video"
      referrerPolicy="no-referrer-when-downgrade"
      sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
      loading="lazy"
    />
  );
};

const EXTRA_PROPS = [
  'isMediaEmbed', 'mediaType', 'mediaUrl', 'mediaAutoplay', 'mediaHidden',
  'animationType', 'animationDuration', 'animationDelay',
  'isLabeledShape',
  'isTable', 'tableRows', 'tableCols', 'isTableCell', 'isTableText', 'cellRow', 'cellCol',
];

// Suppress Fabric.js image loading errors (CORS issues with cross-origin images)
// This prevents the React error overlay from blocking the app
if (typeof window !== 'undefined') {
  const suppressFabricError = (e) => {
    const msg = e.message || e.reason?.message || '';
    if (msg.includes('fabric: Error loading') || msg.includes('fabric:')) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
      console.warn('Fabric image loading error (likely CORS):', msg);
      return true;
    }
  };
  window.addEventListener('error', suppressFabricError);
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || String(e.reason) || '';
    if (msg.includes('fabric') || msg.includes('Error loading')) {
      e.preventDefault();
      console.warn('Fabric promise rejection (likely CORS):', msg);
    }
  });
}

const fixInteractiveGroups = (canvas) => {
  canvas.getObjects().forEach((obj) => {
    if (obj.type === 'group' && (obj.isLabeledShape || obj.isTable)) {
      obj.set({ interactive: true, subTargetCheck: true });
    }
  });
};

const restoreCustomProps = (canvas, rawData) => {
  const objs = canvas.getObjects();
  const rawObjs = rawData?.objects;
  if (!rawObjs) return;
  rawObjs.forEach((raw, i) => {
    if (i >= objs.length) return;
    EXTRA_PROPS.forEach((prop) => {
      if (raw[prop] !== undefined) objs[i][prop] = raw[prop];
    });
  });
};

const extractMediaFromCanvas = (canvas) => {
  const items = [];
  canvas.getObjects().forEach((obj) => {
    if (obj.isMediaEmbed) {
      const bound = obj.getBoundingRect();
      items.push({
        type: obj.mediaType,
        url: obj.mediaUrl,
        autoplay: !!obj.mediaAutoplay,
        hidden: !!obj.mediaHidden,
        left: bound.left,
        top: bound.top,
        width: bound.width,
        height: bound.height,
      });
    }
  });
  return items;
};

export const SlideViewer = ({ content, onSlideProgress }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const fcRef = useRef(null);
  const fsCanvasRef = useRef(null);
  const fsFcRef = useRef(null);

  const [slides, setSlides] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [scale, setScale] = useState(1);
  const [mediaDialog, setMediaDialog] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [transAnim, setTransAnim] = useState('');
  
  /* Track visited slides */
  const [visitedSlides, setVisitedSlides] = useState(new Set([0])); // Start with first slide visited

  /* Fullscreen state */
  const [fullscreen, setFullscreen] = useState(false);
  const [fsIdx, setFsIdx] = useState(0);
  const [fsMediaItems, setFsMediaItems] = useState([]);
  const [fsTransAnim, setFsTransAnim] = useState('');
  const [fsScale, setFsScale] = useState(1);
  const fsContainerRef = useRef(null);  /* Ref to measure fullscreen container */

  /* Slideshow state */
  const [slideshow, setSlideshow] = useState(false);
  const slideshowTimer = useRef(null);
  const fsAudioRefs = useRef([]);  /* Track fullscreen audio elements for slideshow sync */

  /* Mobile detection state */
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);

  // Detect mobile portrait mode
  useEffect(() => {
    const checkMobilePortrait = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      // Mobile portrait: width < 768px AND height > width (portrait orientation)
      setIsMobilePortrait(width < 768 && height > width);
    };
    
    checkMobilePortrait();
    window.addEventListener('resize', checkMobilePortrait);
    window.addEventListener('orientationchange', () => {
      // Small delay for orientation change to complete
      setTimeout(checkMobilePortrait, 100);
    });
    
    return () => {
      window.removeEventListener('resize', checkMobilePortrait);
      window.removeEventListener('orientationchange', checkMobilePortrait);
    };
  }, []);

  // Parse content
  useEffect(() => {
    try {
      const data = typeof content === 'string' ? JSON.parse(content) : content;
      if (data?.slides?.length) {
        setSlides(data.slides);
        // Reset visited slides when content changes
        setVisitedSlides(new Set([0]));
      }
    } catch {
      setSlides([]);
    }
  }, [content]);

  // Track visited slides and report progress to parent
  useEffect(() => {
    // Mark current slide as visited
    setVisitedSlides(prev => {
      const newSet = new Set(prev);
      newSet.add(activeIdx);
      return newSet;
    });
  }, [activeIdx]);

  // Report slide progress to parent component
  useEffect(() => {
    if (onSlideProgress && slides.length > 0) {
      const lastSlideReached = visitedSlides.has(slides.length - 1);
      const visitedCount = visitedSlides.size;
      onSlideProgress({
        currentSlide: activeIdx + 1,
        totalSlides: slides.length,
        visitedSlides: visitedCount,
        lastSlideReached,
        allSlidesVisited: visitedCount >= slides.length
      });
    }
  }, [activeIdx, slides.length, visitedSlides, onSlideProgress]);

  // Init canvas
  useEffect(() => {
    if (!canvasRef.current || slides.length === 0) return;
    if (fcRef.current) { fcRef.current.dispose(); fcRef.current = null; }
    const c = new fabric.StaticCanvas(canvasRef.current, { width: CW, height: CH });
    fcRef.current = c;

    const loadSlide = async () => {
      const data = slides[0]?.canvas;
      if (data) {
        try {
          await c.loadFromJSON(data);
        } catch (err) {
          console.warn('Slide load error (likely CORS image):', err?.message || err);
        }
        restoreCustomProps(c, data);
        fixInteractiveGroups(c);
        c.renderAll();
        setMediaItems(extractMediaFromCanvas(c));
        runObjectAnimations(c);
      }
    };
    loadSlide();
    return () => { c.dispose(); fcRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides]);

  // Scale
  useEffect(() => {
    if (!containerRef.current) return;
    const calc = () => {
      const el = containerRef.current;
      if (!el) return;
      setScale(Math.min(el.clientWidth / CW, (el.clientHeight || 400) / CH, 1));
    };
    calc();
    const obs = new ResizeObserver(calc);
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Navigate slides (inline)
  const goTo = useCallback(async (idx) => {
    if (!fcRef.current || idx < 0 || idx >= slides.length) return;
    const slide = slides[idx];
    const trans = slide?.transition;
    fcRef.current.clear();
    fcRef.current.backgroundColor = '#ffffff';
    const data = slide?.canvas;
    if (data) {
      try {
        await fcRef.current.loadFromJSON(data);
      } catch (err) {
        console.warn('Inline nav load error (likely CORS):', err?.message || err);
      }
      restoreCustomProps(fcRef.current, data);
      fixInteractiveGroups(fcRef.current);
    }
    fcRef.current.renderAll();
    setMediaItems(extractMediaFromCanvas(fcRef.current));
    setActiveIdx(idx);
    const anim = getTransitionAnimation(trans);
    if (anim) {
      setTransAnim(anim);
      setTimeout(() => setTransAnim(''), (trans?.duration || 500) + 100);
    } else {
      setTransAnim('');
    }
    runObjectAnimations(fcRef.current);
  }, [slides]);

  // Keyboard nav (inline, not when fullscreen)
  useEffect(() => {
    if (fullscreen) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goTo(activeIdx + 1);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') goTo(activeIdx - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeIdx, goTo, fullscreen]);

  /* ── Fullscreen ── */
  const fsOverlayRef = useRef(null);
  
  // Detect if running in Capacitor (iOS/Android native app)
  const isCapacitor = typeof window !== 'undefined' && 
    (window.Capacitor !== undefined || 
     window.location.protocol === 'capacitor:' ||
     navigator.userAgent.includes('Capacitor'));
  
  const openFullscreen = useCallback(async () => {
    /* Stop inline media by clearing items (removes HTML audio/video elements) */
    setMediaItems([]);
    setFsIdx(activeIdx);
    setFullscreen(true);
    
    /* Skip browser Fullscreen API on Capacitor to avoid "localhost is in fullscreen" popup */
    /* Our CSS overlay already provides fullscreen experience */
    if (isCapacitor) {
      // Just try orientation lock without browser fullscreen
      try {
        if (screen.orientation && screen.orientation.lock) {
          await screen.orientation.lock('landscape').catch(() => {});
        }
      } catch (e) {
        // Orientation lock not supported
      }
      return;
    }
    
    /* Request browser fullscreen + landscape orientation for web browsers */
    /* Small delay to ensure the overlay element is rendered first */
    setTimeout(async () => {
      try {
        const elem = fsOverlayRef.current || document.documentElement;
        
        /* Request browser fullscreen first (required for orientation lock) */
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
        
        /* Then try to lock orientation to landscape */
        if (screen.orientation && screen.orientation.lock) {
          await screen.orientation.lock('landscape').catch(() => {
            // Silently ignore - orientation lock may not be supported
          });
        }
      } catch (e) {
        // Fullscreen/orientation not supported or denied - continue with overlay mode
        console.log('Fullscreen API not available, using overlay mode');
      }
    }, 100);
  }, [activeIdx, isCapacitor]);

  const closeFullscreen = useCallback(() => {
    setSlideshow(false);
    setFullscreen(false);
    
    /* Exit browser fullscreen and unlock orientation (skip on Capacitor) */
    if (!isCapacitor) {
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        } else if (document.webkitFullscreenElement) {
          document.webkitExitFullscreen();
        }
      } catch (e) {
        // Fullscreen API not supported
      }
    }
    
    try {
      if (screen.orientation && screen.orientation.unlock) {
        screen.orientation.unlock();
      }
    } catch (e) {
      // Orientation API not supported
    }
    
    /* Re-extract inline media on close */
    if (fcRef.current) setMediaItems(extractMediaFromCanvas(fcRef.current));
  }, [isCapacitor]);

  // Init fullscreen canvas
  useEffect(() => {
    if (!fullscreen) {
      if (fsFcRef.current) { fsFcRef.current.dispose(); fsFcRef.current = null; }
      return;
    }
    if (!fsCanvasRef.current) return;
    const pc = new fabric.StaticCanvas(fsCanvasRef.current, { width: CW, height: CH });
    fsFcRef.current = pc;
    const data = slides[fsIdx]?.canvas;
    if (data) {
      pc.loadFromJSON(data).then(() => {
        restoreCustomProps(pc, data);
        fixInteractiveGroups(pc);
        pc.renderAll();
        setFsMediaItems(extractMediaFromCanvas(pc));
        runObjectAnimations(pc);
      }).catch((err) => {
        console.warn('Fullscreen slide load error (likely CORS):', err?.message || err);
        // Still render whatever loaded
        pc.renderAll();
        setFsMediaItems(extractMediaFromCanvas(pc));
      });
    }
    return () => { pc.dispose(); fsFcRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen]);

  // Calculate fullscreen scale to fit container
  // Uses window dimensions directly for maximum reliability across environments
  useEffect(() => {
    if (!fullscreen) return;
    
    const CONTROLS_BAR_HEIGHT = 56; // Slightly smaller on mobile
    
    const calculateScale = () => {
      // Use window dimensions directly - more reliable across environments
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Detect if we're in landscape mode on mobile
      const isMobile = Math.min(viewportWidth, viewportHeight) < 500;
      const isLandscape = viewportWidth > viewportHeight;
      const isMobileLandscape = isMobile && isLandscape;
      
      // Use minimal padding on mobile, especially in landscape
      // Add extra safety margin to prevent edge clipping
      let horizontalPadding, verticalPadding;
      if (isMobileLandscape) {
        horizontalPadding = 16; // Slightly more padding to prevent clipping
        verticalPadding = 8;
      } else if (isMobile) {
        horizontalPadding = 24;
        verticalPadding = 12;
      } else {
        horizontalPadding = 64;
        verticalPadding = 32;
      }
      
      const availableWidth = viewportWidth - horizontalPadding;
      const availableHeight = viewportHeight - CONTROLS_BAR_HEIGHT - verticalPadding;
      
      const scaleX = availableWidth / CW;
      const scaleY = availableHeight / CH;
      
      // Use the smaller scale to ensure slide fits completely
      // Apply a 0.98 factor for safety margin to prevent any edge clipping
      const newScale = Math.min(scaleX, scaleY) * 0.98;
      
      // Cap the scale to prevent over-scaling
      const maxScale = isMobileLandscape ? 1.2 : (isMobile ? 1.0 : 1.0);
      setFsScale(Math.min(newScale, maxScale));
    };
    
    // Initial calculation with slight delay for DOM to settle
    const initialTimer = setTimeout(calculateScale, 50);
    // Recalculate again after orientation animation completes
    const secondTimer = setTimeout(calculateScale, 300);
    
    // Also recalculate on orientation change
    const handleOrientationChange = () => {
      // Multiple timeouts to handle orientation animation at different stages
      setTimeout(calculateScale, 100);
      setTimeout(calculateScale, 300);
      setTimeout(calculateScale, 500);
    };
    
    window.addEventListener('resize', calculateScale);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Also listen for fullscreen changes
    const handleFullscreenChange = () => {
      setTimeout(calculateScale, 100);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(secondTimer);
      window.removeEventListener('resize', calculateScale);
      window.removeEventListener('orientationchange', handleOrientationChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [fullscreen]);

  // Fullscreen slide navigation
  useEffect(() => {
    if (!fullscreen || !fsFcRef.current) return;
    
    /* Guard: check canvas is still valid before clearing */
    if (!fsFcRef.current.lowerCanvasEl) return;
    
    /* Clear audio refs when slide changes */
    fsAudioRefs.current = [];
    
    /* Track visited slide in fullscreen mode */
    setVisitedSlides(prev => {
      const newSet = new Set(prev);
      newSet.add(fsIdx);
      return newSet;
    });
    
    const slide = slides[fsIdx];
    const trans = slide?.transition;
    let transTimer;
    
    try {
      fsFcRef.current.clear();
      fsFcRef.current.backgroundColor = '#ffffff';
    } catch (e) {
      console.warn('Canvas clear failed:', e);
      return;
    }
    
    const data = slide?.canvas;
    setFsMediaItems([]);

    if (data) {
      fsFcRef.current.loadFromJSON(data).then(() => {
        if (!fsFcRef.current) return;
        restoreCustomProps(fsFcRef.current, data);
        fixInteractiveGroups(fsFcRef.current);
        fsFcRef.current.renderAll();
        setFsMediaItems(extractMediaFromCanvas(fsFcRef.current));
        const anim = getTransitionAnimation(trans);
        if (anim) {
          setFsTransAnim(anim);
          transTimer = setTimeout(() => setFsTransAnim(''), (trans?.duration || 500) + 100);
        } else {
          setFsTransAnim('');
        }
        runObjectAnimations(fsFcRef.current);
      }).catch((err) => {
        console.warn('Fullscreen nav load error (likely CORS):', err?.message || err);
        if (fsFcRef.current) {
          fsFcRef.current.renderAll();
          setFsMediaItems(extractMediaFromCanvas(fsFcRef.current));
        }
        setFsTransAnim('');
      });
    } else {
      fsFcRef.current.renderAll();
      setFsTransAnim('');
    }
    return () => { if (transTimer) clearTimeout(transTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsIdx]);

  // Fullscreen keyboard nav
  useEffect(() => {
    if (!fullscreen) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFsIdx((p) => Math.min(slides.length - 1, p + 1));
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFsIdx((p) => Math.max(0, p - 1));
      }
      if (e.key === 'Escape') closeFullscreen();
      if (e.key === ' ') { e.preventDefault(); setSlideshow((p) => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fullscreen, slides.length, closeFullscreen]);

  // Listen for browser fullscreen exit (user presses Escape or swipes down on mobile)
  useEffect(() => {
    if (!fullscreen) return;
    
    const handleBrowserFullscreenExit = () => {
      // If browser exits fullscreen but our state still shows fullscreen, sync it
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        // Small delay to avoid race conditions with our own closeFullscreen call
        setTimeout(() => {
          if (fullscreen) {
            setSlideshow(false);
            setFullscreen(false);
            if (fcRef.current) setMediaItems(extractMediaFromCanvas(fcRef.current));
          }
        }, 50);
      }
    };
    
    document.addEventListener('fullscreenchange', handleBrowserFullscreenExit);
    document.addEventListener('webkitfullscreenchange', handleBrowserFullscreenExit);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleBrowserFullscreenExit);
      document.removeEventListener('webkitfullscreenchange', handleBrowserFullscreenExit);
    };
  }, [fullscreen]);

  /* ── Slideshow auto-advance ── */
  useEffect(() => {
    if (!slideshow || !fullscreen) {
      if (slideshowTimer.current) {
        clearTimeout(slideshowTimer.current);
        slideshowTimer.current = null;
      }
      return;
    }
    
    /* Check if any audio is currently playing */
    const isAudioPlaying = () => {
      const audios = fsAudioRefs.current.filter(a => a);
      return audios.some(audio => !audio.paused && !audio.ended);
    };
    
    /* Get the longest remaining audio duration */
    const getMaxRemainingAudioTime = () => {
      let maxRemaining = 0;
      fsAudioRefs.current.forEach(audio => {
        if (audio && !audio.paused && !audio.ended && audio.duration && isFinite(audio.duration)) {
          const remaining = (audio.duration - audio.currentTime) * 1000;
          if (remaining > maxRemaining) maxRemaining = remaining;
        }
      });
      return maxRemaining;
    };
    
    const advanceSlide = () => {
      /* If audio is playing, wait for it to finish */
      if (isAudioPlaying()) {
        const remaining = getMaxRemainingAudioTime();
        if (remaining > 500) {
          /* Schedule re-check when audio should be finishing */
          slideshowTimer.current = setTimeout(advanceSlide, Math.min(remaining + 200, 30000));
          return;
        }
      }
      
      /* Advance to next slide or stop slideshow */
      setFsIdx((p) => {
        if (p >= slides.length - 1) { 
          setSlideshow(false); 
          return p; 
        }
        return p + 1;
      });
    };
    
    /* Start the slideshow timer - 5 seconds default */
    slideshowTimer.current = setTimeout(advanceSlide, 5000);
    
    return () => { 
      if (slideshowTimer.current) {
        clearTimeout(slideshowTimer.current);
        slideshowTimer.current = null;
      }
    };
  }, [slideshow, fullscreen, slides.length, fsIdx]);

  if (!slides.length) {
    return <div className="p-6 text-center text-muted-foreground" data-testid="slide-viewer-empty">No slides to display</div>;
  }

  /** Render a media overlay item - with YouTube/Vimeo support and minimal controls */
  const renderMediaOverlay = (item, i, prefix) =>
    item.hidden ? (
      <audio key={i} src={item.url} autoPlay={item.autoplay} style={{ display: 'none' }} data-testid={`${prefix}-audio-hidden-${i}`} />
    ) : (
      <div key={i} className="absolute" style={{
        left: item.left, top: item.top, width: item.width, height: item.height,
      }}>
        {item.type === 'video' ? (
          /* Check if it's a YouTube/Vimeo URL - use iframe embed */
          isEmbedUrl(item.url) ? (
            <EmbedPlayer url={item.url} testId={`${prefix}-embed-${i}`} />
          ) : (
            /* Regular video - use minimal custom player (play/pause only) */
            <MinimalVideoPlayer 
              src={item.url} 
              autoPlay={item.autoplay} 
              testId={`${prefix}-video-${i}`}
            />
          )
        ) : (
          /* Audio: clean player with no background - just controls */
          <div className="w-full h-full flex items-center justify-center">
            <audio 
              src={item.url} 
              controlsList="nodownload noplaybackrate" 
              disablePictureInPicture
              autoPlay={item.autoplay} 
              controls
              className="w-full" 
              style={{ minWidth: 0, height: '100%' }} 
              data-testid={`${prefix}-audio-${i}`} 
            />
          </div>
        )}
      </div>
    );

  /** Render fullscreen media overlay with audio tracking for slideshow sync */
  const renderFsMediaOverlay = (item, i) =>
    item.hidden ? (
      <audio 
        key={i} 
        src={item.url} 
        autoPlay={item.autoplay} 
        style={{ display: 'none' }} 
        data-testid={`fs-audio-hidden-${i}`}
        ref={(el) => { if (el && item.type === 'audio') fsAudioRefs.current[i] = el; }}
      />
    ) : (
      <div key={i} className="absolute" style={{
        left: item.left, top: item.top, width: item.width, height: item.height,
      }}>
        {item.type === 'video' ? (
          /* Check if it's a YouTube/Vimeo URL - use iframe embed */
          isEmbedUrl(item.url) ? (
            <EmbedPlayer url={item.url} testId={`fs-embed-${i}`} />
          ) : (
            /* Regular video - use minimal custom player (play/pause only) */
            <MinimalVideoPlayer 
              src={item.url} 
              autoPlay={item.autoplay} 
              testId={`fs-video-${i}`}
            />
          )
        ) : (
          /* Audio: clean player with ref tracking for slideshow sync */
          <div className="w-full h-full flex items-center justify-center">
            <audio 
              src={item.url} 
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              autoPlay={item.autoplay} 
              controls
              className="w-full" 
              style={{ minWidth: 0, height: '100%' }} 
              data-testid={`fs-audio-${i}`}
              ref={(el) => { if (el) fsAudioRefs.current[i] = el; }}
            />
          </div>
        )}
      </div>
    );

  return (
    <div className="space-y-3" data-testid="slide-viewer">
      <style>{TRANSITION_CSS}</style>

      {/* Mobile Portrait Placeholder - prompts user to open fullscreen */}
      {isMobilePortrait ? (
        <div
          className="relative bg-neutral-100 dark:bg-neutral-900 rounded-lg overflow-hidden flex flex-col items-center justify-center p-6"
          style={{ minHeight: 280 }}
          data-testid="slide-viewer-mobile-placeholder"
        >
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <Maximize2 className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-1">View Presentation</h3>
              <p className="text-sm text-muted-foreground mb-4">
                For the best viewing experience, open in full screen mode
              </p>
            </div>
            <Button onClick={openFullscreen} className="gap-2" data-testid="mobile-fullscreen-btn">
              <Maximize2 className="w-4 h-4" />
              Open Full Screen
            </Button>
            <p className="text-xs text-muted-foreground">
              {slides.length} slide{slides.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      ) : (
        /* Desktop/Tablet Inline viewer */
        <div
          ref={containerRef}
          className="relative bg-neutral-100 dark:bg-neutral-900 rounded-lg overflow-hidden flex items-center justify-center"
          style={{ minHeight: Math.min(CH * scale + 16, 400) }}
        >
          <div className="relative" style={{
            transform: `scale(${scale})`, transformOrigin: 'center center',
            ...(transAnim ? { animation: transAnim } : {}),
          }}>
            <canvas ref={canvasRef} data-testid="slide-viewer-canvas" />
            {/* Inline media overlays — hidden when fullscreen to prevent double audio */}
            {!fullscreen && mediaItems.map((item, i) => renderMediaOverlay(item, i, 'viewer'))}
          </div>
        </div>
      )}

      {/* Controls bar - hidden on mobile portrait since we show the fullscreen button in the placeholder */}
      {!isMobilePortrait && (
        <div className="flex items-center justify-between" data-testid="slide-controls">
          <div className="flex items-center gap-4">
            {slides.length > 1 && (
              <>
                <Button variant="outline" size="icon" onClick={() => goTo(activeIdx - 1)} disabled={activeIdx === 0} data-testid="viewer-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground" data-testid="viewer-counter">
                  Slide {activeIdx + 1} of {slides.length}
                </span>
                <Button variant="outline" size="icon" onClick={() => goTo(activeIdx + 1)} disabled={activeIdx === slides.length - 1} data-testid="viewer-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={openFullscreen} data-testid="viewer-fullscreen-btn">
            <Maximize2 className="w-4 h-4 mr-2" />
            Full Screen
          </Button>
        </div>
      )}

      {/* Fullscreen overlay - using inline styles with maximum specificity */}
      {fullscreen && (
        <div 
          ref={fsOverlayRef}
          data-testid="viewer-fullscreen"
          style={{
            position: 'fixed',
            top: '0px',
            left: '0px',
            right: '0px',
            bottom: '0px',
            width: '100vw',
            height: '100vh',
            zIndex: 999999,
            backgroundColor: '#000000',
            overflow: 'hidden',
          }}
        >
          <style>{TRANSITION_CSS}</style>
          
          {/* Close button */}
          <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 60 }}>
            <Button variant="ghost" size="icon" onClick={closeFullscreen} className="text-white hover:bg-white/20" data-testid="fs-close">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Main slide area - takes full height minus controls bar */}
          <div 
            ref={fsContainerRef} 
            style={{
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              bottom: '56px', /* Reduced controls bar height for mobile */
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px', /* Minimal padding to maximize slide area */
              overflow: 'hidden',
              backgroundColor: '#000000',
            }}
          >
            {/* Left click zone - for previous slide */}
            <div 
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '25%',
                cursor: 'pointer',
                zIndex: 40,
              }}
              onClick={() => fsIdx > 0 && setFsIdx(fsIdx - 1)}
              data-testid="fs-click-prev"
            />
            
            {/* Right click zone - for next slide or close on last */}
            <div 
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '25%',
                cursor: 'pointer',
                zIndex: 40,
              }}
              onClick={() => {
                if (fsIdx >= slides.length - 1) {
                  closeFullscreen();
                } else {
                  setFsIdx(fsIdx + 1);
                }
              }}
              data-testid="fs-click-next"
            />
            
            {/* Slide canvas */}
            <div 
              style={{
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                borderRadius: '0.5rem',
                position: 'relative',
                zIndex: 30,
                transform: `scale(${fsScale})`,
                transformOrigin: 'center center',
                /* Explicit dimensions to prevent any sizing issues */
                width: `${CW}px`,
                height: `${CH}px`,
                flexShrink: 0,
                ...(fsTransAnim ? { animation: fsTransAnim } : {}),
              }}
            >
              {/* Inner container for overflow clipping (for rounded corners) */}
              <div style={{
                width: '100%',
                height: '100%',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                position: 'relative',
              }}>
                <canvas ref={fsCanvasRef} />
                {fsMediaItems.map((item, i) => renderFsMediaOverlay(item, i))}
              </div>
            </div>
          </div>
          
          {/* Controls bar - ABSOLUTELY positioned at bottom, compact for mobile */}
          <div 
            style={{
              position: 'absolute',
              left: '0px',
              right: '0px',
              bottom: '0px',
              height: '56px', /* Compact height */
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px', /* Compact gap */
              zIndex: 50,
              backgroundColor: '#000000', /* Solid black background */
              padding: '0 8px',
            }}
          >
            <Button variant="ghost" size="icon"
              onClick={() => setFsIdx(Math.max(0, fsIdx - 1))} disabled={fsIdx === 0}
              className="text-white hover:bg-white/20" data-testid="fs-prev">
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <span className="text-white text-sm" data-testid="fs-counter">
              Slide {fsIdx + 1} of {slides.length}
            </span>
            <Button variant="ghost" size="icon"
              onClick={() => {
                if (fsIdx >= slides.length - 1) {
                  closeFullscreen();
                } else {
                  setFsIdx(fsIdx + 1);
                }
              }}
              className="text-white hover:bg-white/20" data-testid="fs-next">
              <ChevronRight className="w-6 h-6" />
            </Button>

            {/* Slideshow toggle */}
            <div className="border-l border-white/20 pl-5 ml-2 flex items-center gap-2">
              <Button variant={slideshow ? 'default' : 'ghost'} size="sm"
                onClick={() => setSlideshow((p) => !p)}
                className={slideshow ? 'bg-white text-black hover:bg-white/90' : 'text-white hover:bg-white/20'}
                data-testid="fs-slideshow">
                {slideshow ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                <span className="hidden sm:inline">{slideshow ? 'Pause' : 'Slideshow'}</span>
              </Button>
            </div>

            {/* Keyboard shortcut hint - hidden on mobile */}
            <span className="text-white/40 text-[10px] ml-4 hidden sm:inline">Arrows / Space / Esc</span>
          </div>

          {/* Slideshow progress indicator */}
          {slideshow && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }}>
              <div style={{
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                width: `${((fsIdx + 1) / slides.length) * 100}%`,
                transition: 'width 0.3s ease',
              }} data-testid="fs-slideshow-progress" />
            </div>
          )}
        </div>
      )}

      {/* Media Dialog (fallback) */}
      <Dialog open={!!mediaDialog} onOpenChange={() => setMediaDialog(null)}>
        <DialogContent className="max-w-2xl">
          {mediaDialog?.type === 'video' && (
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Video</h3>
              {isEmbedUrl(mediaDialog.url) ? (
                <div className="w-full aspect-video">
                  <EmbedPlayer url={mediaDialog.url} testId="media-embed-player" />
                </div>
              ) : (
                <MinimalVideoPlayer 
                  src={mediaDialog.url} 
                  autoPlay={mediaDialog.autoplay} 
                  testId="media-video-player"
                  className="w-full rounded-lg"
                />
              )}
            </div>
          )}
          {mediaDialog?.type === 'audio' && (
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Audio</h3>
              <audio 
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                controls 
                autoPlay={mediaDialog.autoplay} 
                className="w-full" 
                data-testid="media-audio-player"
              >
                <source src={mediaDialog.url} />
              </audio>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
